import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Tags, Plus, Trash2, ArrowUp, ArrowDown, Clock } from "lucide-react";
import { useCategories } from "@/components/categories";

const CategoryRow = ({ cat, onSaved, onMove, isFirst, isLast }) => {
  const [name, setName] = useState(cat.name);
  const [dur, setDur] = useState(cat.default_duration ?? "");
  const save = async (patch) => {
    try {
      await api.put(`/categories/${cat.id}`, { name, color: cat.color, default_duration: cat.default_duration ?? null, ...patch });
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'enregistrement");
    }
  };
  const saveDuration = () => {
    const v = dur === "" ? null : Math.min(600, Math.max(1, Number(dur)));
    if (v !== (cat.default_duration ?? null)) save({ default_duration: v });
  };
  const applyAll = async () => {
    try {
      const { data } = await api.post(`/categories/${cat.id}/apply-duration`);
      toast.success(`${cat.default_duration}s appliquées à ${data.updated} image(s) « ${cat.name} »`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    }
  };
  const remove = async () => {
    await api.delete(`/categories/${cat.id}`);
    toast.success(`Catégorie « ${cat.name} » supprimée`);
    onSaved();
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-2" data-testid={`category-row-${cat.id}`}>
      <div className="flex flex-col">
        <Button size="icon" variant="ghost" className="h-5 w-7" disabled={isFirst} onClick={() => onMove(-1)} title="Monter" data-testid={`category-up-${cat.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" className="h-5 w-7" disabled={isLast} onClick={() => onMove(1)} title="Descendre" data-testid={`category-down-${cat.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
      </div>
      <input type="color" value={cat.color} onChange={(e) => save({ color: e.target.value })}
        className="h-9 w-9 cursor-pointer rounded-md border-0 bg-transparent" data-testid={`category-color-${cat.id}`} />
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== cat.name && save({ name })}
        className="min-w-[140px] flex-1" data-testid={`category-name-${cat.id}`} />
      <div className="flex items-center gap-1" title="Durée d'affichage par défaut (secondes)">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <Input type="number" min={1} max={600} value={dur} placeholder="—" onChange={(e) => setDur(e.target.value)} onBlur={saveDuration}
          className="w-16 px-2 text-center" data-testid={`category-duration-${cat.id}`} />
        <span className="text-xs text-muted-foreground">s</span>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="secondary" disabled={!cat.default_duration} data-testid={`category-apply-duration-${cat.id}`}
            title="Appliquer cette durée à toutes les images de la catégorie">Appliquer à toutes</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Appliquer {cat.default_duration}s à toutes les images « {cat.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>La durée d'affichage de toutes les images de cette catégorie sera remplacée, dans tous les bandeaux (y compris celui de l'écran TV).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={applyAll} data-testid={`category-confirm-apply-duration-${cat.id}`}>Appliquer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`category-delete-${cat.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {cat.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>Les images de cette catégorie passeront en « Sans catégorie ». Elles ne sont pas supprimées.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`category-confirm-delete-${cat.id}`}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const CategoriesSection = () => {
  const { categories, reload } = useCategories();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#8b5cf6");

  const move = async (i, d) => {
    const ids = categories.map((c) => c.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try {
      await api.put("/categories/order", { ids });
      reload();
      toast.success("Ordre de passage mis à jour dans les bandeaux");
    } catch {
      toast.error("Échec du changement d'ordre");
    }
  };

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/categories", { name: name.trim(), color });
      setName("");
      toast.success("Catégorie ajoutée");
      reload();
    } catch {
      toast.error("Échec de l'ajout");
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6" data-testid="categories-section">
      <div className="mb-1 flex items-center gap-2">
        <Tags className="h-5 w-5 text-primary" />
        <h2 className="font-display text-lg font-semibold">Catégories d'images</h2>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Classez les images de vos bandeaux (sponsors, informations…). Le choix se fait sur chaque image dans l'éditeur de bandeau. Le bandeau passe les catégories dans l'ordre ci-dessous (flèches ↑ ↓), les images sans catégorie en dernier.</p>
      <div className="space-y-2">
        {categories.map((c, i) => (
          <CategoryRow key={`${c.id}-${c.name}-${c.default_duration}`} cat={c} onSaved={reload} onMove={(d) => move(i, d)}
            isFirst={i === 0} isLast={i === categories.length - 1} />
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-9 cursor-pointer rounded-md border-0 bg-transparent" data-testid="new-category-color" />
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Nouvelle catégorie (ex. Événements)" className="flex-1" data-testid="new-category-name" />
        <Button onClick={add} disabled={!name.trim()} data-testid="add-category-button"><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
      </div>
    </section>
  );
};
